/* -----------------Types--------------- */

import { getNumPercenetageAnswered } from "@features/exam/helpers";
import useAIFeedbackStore from "@features/session/hooks/useAIFeedbackStore";
import type { Choice } from "@memorang/types";
import Box from "@memorang/ui/components/Box";
import { memo, useEffect, useState } from "react";
import { DraggableList } from "../itemtypes/question/variants/Sequence";
/* -----------------Components--------------- */
import ChoiceListItem from "./ChoiceListItem";
interface Props {
	choices: Choice[];
	handleSelectChoice: (
		choiceId: string,
		crossingOut?: boolean,
		currentOrders?: string[],
	) => void;
	selectedChoiceIds?: string[];
	correctAnswerIds?: string[];
	crossedOutChoiceIds?: string[];
	multiSelect?: boolean;
	isAnswered?: boolean;
	showCrossOut?: boolean;
	isSequence?: boolean;
	itemId: number;
	correctAnswerOrders?: string[];
	maxChoiceCount?: number;
	isSummativeTest?: boolean;
}

const ChoicesList = ({
	choices,
	handleSelectChoice,
	selectedChoiceIds,
	multiSelect,
	correctAnswerIds,
	crossedOutChoiceIds,
	isAnswered,
	showCrossOut,
	isSequence,
	itemId,
	correctAnswerOrders,
	maxChoiceCount,
	isSummativeTest,
}: Props) => {
	const userResponse = useAIFeedbackStore((store) => store.userResponse);
	const setUserResponse = useAIFeedbackStore((store) => store.setUserResponse);

	const currentUserResponse = userResponse?.[itemId];

	useEffect(() => {
		if (isSequence && !currentUserResponse) {
			setUserResponse(
				itemId,
				choices.map((choice) => choice.text),
			);
		}
	}, [choices, itemId, setUserResponse, isSequence, currentUserResponse]);

	const [localChoices, setLocalChoices] = useState(choices);

	if (isSequence) {
		const renderItem = (choice: Choice, index: number, drag: () => void) => {
			const isCorrectAnswer = correctAnswerOrders?.[index] === choice.id;
			const status = selectedChoiceIds?.includes(choice.id)
				? "checked"
				: "unchecked";
			const percentageAnswered = getNumPercenetageAnswered(
				choices,
				choice.numFirstSelections ?? 0,
				isAnswered,
			);

			const isSelected = selectedChoiceIds?.includes(choice.id);

			return (
				<ChoiceListItem
					{...choice}
					text={choice.text!}
					index={index + 1}
					useCardWrapper
					answered={isAnswered}
					multiSelect={multiSelect}
					status={status}
					isSelected={isSelected}
					showCrossOut={showCrossOut}
					isCorrectAnswer={isCorrectAnswer}
					drag={drag}
					percentageAnswered={percentageAnswered}
					handleSelectChoice={(_crossOut) => {
						// handleSelectChoice(choice.id, crossOut);
					}}
				/>
			);
		};

		const handleReorder = (newItems: Choice[]) => {
			setLocalChoices(newItems);
			setUserResponse(
				itemId,
				newItems.map((item) => item.text),
			);
			handleSelectChoice(
				" ",
				false,
				newItems.map((item) => item.id),
			);
		};

		return (
			<DraggableList
				items={localChoices}
				onReorder={handleReorder}
				renderItem={renderItem}
				answered={isAnswered}
			/>
		);
	}

	return (
		<Box gap={16}>
			{choices?.map((choice, index) => {
				const isCorrectAnswer = correctAnswerIds?.includes(choice.id);

				const status = selectedChoiceIds?.includes(choice.id)
					? "checked"
					: "unchecked";
				const isCrossedOut = crossedOutChoiceIds?.includes(choice.id);

				const percentageAnswered = getNumPercenetageAnswered(
					choices,
					choice.numFirstSelections ?? 0,
					isAnswered,
				);
				const isSelected = selectedChoiceIds?.includes(choice.id);

				const disabled =
					multiSelect &&
					((!!maxChoiceCount && selectedChoiceIds?.length === maxChoiceCount) ||
						selectedChoiceIds?.length === (maxChoiceCount ?? 0)) &&
					!isSelected;

				return (
					<ChoiceListItem
						key={choice.id}
						text={choice.text!}
						{...choice}
						index={index + 1}
						isSummativeTest={isSummativeTest}
						useCardWrapper
						answered={isAnswered}
						disabled={Boolean(disabled)}
						multiSelect={multiSelect}
						status={status}
						isSelected={isSelected}
						showCrossOut={showCrossOut}
						isCorrectAnswer={isCorrectAnswer}
						isCrossedOut={isCrossedOut}
						percentageAnswered={percentageAnswered}
						handleSelectChoice={(crossOut) =>
							handleSelectChoice(choice.id, crossOut)
						}
					/>
				);
			})}
		</Box>
	);
};

export default memo(ChoicesList, (prevProps, nextProps) => {
	return (
		prevProps.selectedChoiceIds === nextProps.selectedChoiceIds &&
		// prevProps.correctAnswerIds === nextProps.correctAnswerIds &&
		// prevProps.crossedOutChoiceIds === nextProps.crossedOutChoiceIds &&
		prevProps.itemId === nextProps.itemId
	);
});
